import React, { useState, useEffect } from 'react';
import { fetchAllReminders, api, getAuthHeaders } from '../utils/api';
import { useUser } from '../redux/useUser';
import Layout from '../components/Layout';
import Loader from '../components/Loader';
import ReminderTable from '../components/ReminderTable';
import ReminderViewModal from '../components/ReminderViewModal';
import ReminderEditModal from '../components/ReminderEditModal';
import ConfirmModal from '../components/ConfirmModal';
import { showSuccessToast, showErrorToast } from '../components/Toast';
import '../styles/notifications.scss';
import Select from 'react-select';

const Reminder = () => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewModal, setViewModal] = useState({ show: false, reminder: null });
  const [editModal, setEditModal] = useState({ show: false, reminder: null });
  const [deleteModal, setDeleteModal] = useState({ show: false, reminderId: null });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filters, setFilters] = useState({ status: 'all', type: 'all', referenceType: 'all' });

  const reminderSelectStyles = {
    control: (base) => ({ ...base, fontSize: '0.875rem', fontWeight: 400, borderColor: '#E0E5F2', borderRadius: '8px', minHeight: '42px', boxShadow: 'none', '&:hover': { borderColor: '#1D19E4' } }),
    option: (base) => ({ ...base, fontSize: '0.875rem', fontWeight: 400 }),
    singleValue: (base) => ({ ...base, fontSize: '0.875rem', fontWeight: 400, color: '#2B3674' }),
    placeholder: (base) => ({ ...base, fontSize: '0.875rem', color: '#B3B3B3' }),
    indicatorSeparator: () => ({ display: 'none' }),
  };

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'overdue', label: 'Overdue' },
  ];

  const typeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'payment', label: 'Payment' },
    { value: 'renewal', label: 'Renewal' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'insurance', label: 'Insurance' },
    { value: 'tax', label: 'Tax' },
    { value: 'other', label: 'Other' },
  ];

  const referenceTypeOptions = [
    { value: 'all', label: 'All References' },
    { value: 'asset', label: 'Asset' },
    { value: 'liability', label: 'Liability' },
  ];
  const { user } = useUser();

  const loadReminders = async () => {
    if (user?.id) {
      setLoading(true);
      setError(null);
      
      try {
        const remindersData = await fetchAllReminders(user.id);
        const rawReminders = remindersData.results || [];
        
        // Backend now populates asset/liability data
        const enriched = rawReminders.map(reminder => {
          // Calculate days until due date
          const dueDate = new Date(reminder.date);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          dueDate.setHours(0, 0, 0, 0);
          
          const diffTime = dueDate - today;
          const daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          const isOverdue = daysUntil < 0 && !reminder.isCompleted;
          
          return {
            ...reminder,
            relatedItemName: reminder.asset?.name || reminder.liability?.name || null,
            relatedItemType: reminder.asset?.assetType || reminder.liability?.liabilityType || null,
            relatedItemValue: reminder.asset?.estimatedValue || reminder.liability?.outstandingAmount || null,
            relatedItemStatus: reminder.asset?.status || reminder.liability?.status || null,
            daysUntil,
            isOverdue
          };
        });
        setReminders(enriched);
      } catch (err) {
        console.error('Error fetching reminders:', err);
        setError('Failed to fetch reminders');
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadReminders();
  }, [user]);

  const markAsComplete = async (reminderId) => {
    try {
      await api.post(`/reminders/${reminderId}/complete`, {}, { headers: getAuthHeaders() });
      showSuccessToast('Reminder marked as complete');
      loadReminders();
    } catch (err) {
      showErrorToast(err?.response?.data?.message || 'Failed to mark reminder as complete');
    }
  };

  const handleView = async (reminder) => {
    try {
      const response = await api.get(`/reminders/${reminder._id || reminder.id}`, { headers: getAuthHeaders() });
      // Merge the enriched data from the list with the fetched data
      const enrichedReminder = {
        ...response.data,
        relatedItemName: response.data.asset?.name || response.data.liability?.name || reminder.relatedItemName,
        relatedItemType: response.data.asset?.assetType || response.data.liability?.liabilityType || reminder.relatedItemType,
        relatedItemValue: response.data.asset?.estimatedValue || response.data.liability?.outstandingAmount || reminder.relatedItemValue,
        relatedItemStatus: response.data.asset?.status || response.data.liability?.status || reminder.relatedItemStatus
      };
      setViewModal({ show: true, reminder: enrichedReminder });
    } catch (err) {
      showErrorToast('Failed to load reminder details');
    }
  };

  const handleEdit = (reminder) => {
    setEditModal({ show: true, reminder });
  };

  const handleSaveEdit = async (reminderId, payload) => {
    try {
      await api.patch(`/reminders/${reminderId}`, payload, { headers: getAuthHeaders() });
      showSuccessToast('Reminder updated successfully');
      setEditModal({ show: false, reminder: null });
      loadReminders();
    } catch (err) {
      showErrorToast(err?.response?.data?.message || 'Failed to update reminder');
    }
  };

  const handleDelete = (reminderId) => {
    setDeleteModal({ show: true, reminderId });
  };

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      await api.delete(`/reminders/${deleteModal.reminderId}`, { headers: getAuthHeaders() });
      showSuccessToast('Reminder deleted successfully');
      setDeleteModal({ show: false, reminderId: null });
      loadReminders();
    } catch (err) {
      showErrorToast(err?.response?.data?.message || 'Failed to delete reminder');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ show: false, reminderId: null });
  };

  const enrichedReminders = reminders;

  // Filter reminders
  const filteredReminders = enrichedReminders.filter(reminder => {
    if (filters.status !== 'all') {
      if (filters.status === 'active' && reminder.isCompleted) return false;
      if (filters.status === 'inactive' && !reminder.isCompleted) return false;
      if (filters.status === 'overdue' && (!reminder.isOverdue || reminder.isCompleted)) return false;
    }
    if (filters.type !== 'all' && reminder.type !== filters.type) return false;
    if (filters.referenceType !== 'all' && reminder.referenceType !== filters.referenceType) return false;
    return true;
  });

  // Sort reminders
  const sortedReminders = [...filteredReminders].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    // Handle null/undefined values
    if (!aValue && !bValue) return 0;
    if (!aValue) return 1;
    if (!bValue) return -1;

    // Handle date sorting
    if (sortConfig.key === 'date' || sortConfig.key === 'createdAt') {
      aValue = new Date(aValue).getTime();
      bValue = new Date(bValue).getTime();
    }

    // Handle string sorting
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = (bValue || '').toLowerCase();
    }

    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  return (
    <Layout>
      <div className="notification-page">
        {loading && <Loader />}
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <div className="notification-list">
          <div style={{
            background: '#fff',
            padding: '20px',
            borderRadius: '15px',
            marginBottom: '10px',
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{ fontSize: '12px', color: '#707EAE', marginBottom: '6px', display: 'block', fontWeight: '600' }}>STATUS</label>
              <Select
                styles={reminderSelectStyles}
                options={statusOptions}
                value={statusOptions.find(o => o.value === filters.status)}
                onChange={(opt) => setFilters(prev => ({ ...prev, status: opt.value }))}
                isSearchable={false}
              />
            </div>

            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{ fontSize: '12px', color: '#707EAE', marginBottom: '6px', display: 'block', fontWeight: '600' }}>TYPE</label>
              <Select
                styles={reminderSelectStyles}
                options={typeOptions}
                value={typeOptions.find(o => o.value === filters.type)}
                onChange={(opt) => setFilters(prev => ({ ...prev, type: opt.value }))}
                isSearchable={false}
              />
            </div>

            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{ fontSize: '12px', color: '#707EAE', marginBottom: '6px', display: 'block', fontWeight: '600' }}>REFERENCE TYPE</label>
              <Select
                styles={reminderSelectStyles}
                options={referenceTypeOptions}
                value={referenceTypeOptions.find(o => o.value === filters.referenceType)}
                onChange={(opt) => setFilters(prev => ({ ...prev, referenceType: opt.value }))}
                isSearchable={false}
              />
            </div>

            {(filters.status !== 'all' || filters.type !== 'all' || filters.referenceType !== 'all') && (
              <button
                onClick={() => setFilters({ status: 'all', type: 'all', referenceType: 'all' })}
                style={{
                  padding: '10px 20px',
                  background: '#F4F7FE',
                  border: '1px solid #E0E5F2',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#1D19E4',
                  cursor: 'pointer',
                  fontWeight: '600',
                  alignSelf: 'flex-end'
                }}
              >
                Clear Filters
              </button>
            )}
          </div>

          {sortedReminders.length === 0 && !loading ? (
            <div className="empty-state" style={{ textAlign: 'center', padding: '3rem 1rem' }}>
              <div style={{ fontSize: '3rem', color: '#e9ecef', marginBottom: '1rem' }}>📅</div>
              <h5 style={{ color: '#2b3674', marginBottom: '0.5rem' }}>No reminders found</h5>
              <p style={{ color: '#707EAE', margin: 0 }}>You don't have any reminders yet</p>
            </div>
          ) : (
            <ReminderTable 
              reminders={sortedReminders}
              onMarkComplete={markAsComplete}
              onView={handleView}
              onEdit={handleEdit}
              onDelete={handleDelete}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          )}
        </div>

        <ReminderViewModal
          show={viewModal.show}
          reminder={viewModal.reminder}
          onClose={() => setViewModal({ show: false, reminder: null })}
        />

        <ReminderEditModal
          show={editModal.show}
          reminder={editModal.reminder}
          onClose={() => setEditModal({ show: false, reminder: null })}
          onSave={handleSaveEdit}
        />

        <ConfirmModal
          show={deleteModal.show}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          loading={deleteLoading}
          title="Delete Reminder"
          message="Are you sure you want to delete this reminder? This action cannot be undone."
          yesLabel="Delete"
          noLabel="Cancel"
        />
      </div>
    </Layout>
  );
};

export default Reminder;